// Reusable formatting helpers

/**
 * Formats a date/time value as 'M/D HH:mm'.
 * - Example: '2025-11-23T16:48' => '11/23 16:48'
 * - (No year, no seconds, always pad hour/minute)
 */
export const formatDateTime = (value: string | number | Date): string => {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${month}/${day} ${hours}:${minutes}`;
};

/**
 * Formats a number with thousands separators and up to two decimal places.
 * Adds an ellipsis ('…') ONLY if additional decimals are present and truncated.
 * - Example: 1322685.11345 => '1,322,685.11…'
 * - Example: 1322685.11 => '1,322,685.11'
 * - Example: 1000 => '1,000'
 */
export const formatAmount = (value: string | number): string => {
  const num = Number(value);
  if (!isFinite(num)) return String(value);
  // Split into integer and decimal parts
  const [intPart, decPart = ''] = num.toString().split('.');
  const intFormatted = Number(intPart).toLocaleString();

  if (decPart.length === 0) return intFormatted;
  if (decPart.length <= 2) return `${intFormatted}.${decPart}`;
  const truncated = decPart.substring(0, 2);
  return `${intFormatted}.${truncated}…`;
};

/**
 * Same as formatAmount, but splits into [main string, needsEllipsis].
 * - e.g., 1322685.1134 -> [ '1,322,685.11', true ]
 * - e.g., 1322685.1 -> [ '1,322,685.1', false ]
 * Use the two parts for tabular right-aligned formatting with trailing ellipsis rendered separately if needed.
 */
export const formatAmountParts = (value: string | number): [string, boolean] => {
  const num = Number(value);
  if (!isFinite(num)) return [String(value), false];
  const [intPart, decPart = ''] = num.toString().split('.');
  const intFormatted = Number(intPart).toLocaleString();
  if (decPart.length === 0) return [intFormatted, false];
  if (decPart.length <= 2) return [`${intFormatted}.${decPart}`, false];
  const truncated = decPart.substring(0, 2);
  return [`${intFormatted}.${truncated}`, true];
};


